// Simple API client for organization onboarding operations
// The actual business logic is in /backend_auth/services/organizationOnboardingService.js
import { API_BASE_URL } from '../config';

const AUTH_API_BASE_URL = API_BASE_URL;

export const onboardingAPI = {
  /**
   * Ensure user has an organization (create/update during onboarding)
   */
  async ensureOrganization(organizationData, userData, surveyData, authToken) {
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    };

    const response = await fetch(`${AUTH_API_BASE_URL}/api/organizations/ensure`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...organizationData,
        userData: userData,
        surveyData: surveyData
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.details || errorData.error || 'Failed to create/update organization';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Complete organization onboarding process
   */
  async completeOnboarding(surveyData, authToken) {
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`
    };

    const response = await fetch(`${AUTH_API_BASE_URL}/api/organizations/complete-onboarding`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        surveyData: surveyData
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.details || errorData.error || 'Failed to complete onboarding';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Get onboarding status for current user's organization
   */
  async getOnboardingStatus(authToken) {
    const headers = {
      'Authorization': `Bearer ${authToken}`
    };

    const response = await fetch(`${AUTH_API_BASE_URL}/api/organizations/onboarding-status`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.details || errorData.error || 'Failed to get onboarding status';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Check if current user can access onboarding
   * Returns true if onboarding is not yet complete
   */
  async canAccessOnboarding(authToken) {
    try {
      const status = await this.getOnboardingStatus(authToken);
      return status.canAccessOnboarding;
    } catch (error) {
      // If there's an error (like no organization), allow onboarding access
      console.log('Error checking onboarding access:', error.message);
      return true;
    }
  },

  /**
   * Full onboarding flow helper - handles organization creation and completion
   * @param {Object} formData - Complete form data from onboarding component
   * @param {string} authToken - Authentication token
   * @returns {Promise<Object>} - Organization and completion data
   */
  async completeFullOnboarding(formData, authToken) {
    try {
      // Step 1: Create/update organization
      const organizationData = {
        name: formData.organizationName || 'My Organization',
        type: 'school'
      };

      const userData = {
        name: formData.name || null
      };

      const surveyData = {
        userType: formData.userType,
        surveyRole: formData.role
      };

      const organization = await this.ensureOrganization(
        organizationData,
        userData,
        surveyData,
        authToken
      );

      // Step 2: Complete onboarding
      const completionResult = await this.completeOnboarding(surveyData, authToken);

      return {
        organization,
        completion: completionResult,
        success: true
      };

    } catch (error) {
      throw new Error(`Onboarding failed: ${error.message}`);
    }
  }
};

export default onboardingAPI;